// 已存搜尋（SRCH-03）：套用、把目前的查詢存起來（個人；org_admin 可存成 org 共用）、刪除。
// 事件頁的「已存搜尋」分頁直接攤開（inline，SPEC EVT-02 頁內分頁、第二輪 R2-P1-3）；不帶 inline 時是按鈕＋彈出面板。
import { useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { mergeSaved, savedSearchApi, type SavedSearch, type SavedSearchFilters } from '../../lib/savedSearch';

interface Props {
  query: string;
  filters: SavedSearchFilters;
  onApply(s: SavedSearch): void;
  /** 直接攤開（事件頁「已存搜尋」分頁） */
  inline?: boolean;
}

export function SavedSearches({ query, filters, onApply, inline }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { planes, orgs } = useScope();
  const lists = useQueries({
    queries: planes.map((p) => ({ queryKey: ['saved-searches', p.api], queryFn: () => savedSearchApi.list(p.api), staleTime: 30_000, retry: false })),
  });
  const all = mergeSaved(lists.map((l) => l.data?.data));
  // 可以存成共用的 org：那個 plane 回報我是 org_admin 的
  const adminOrgs = planes.flatMap((p, i) =>
    (lists[i]?.data?.orgAdminOf ?? []).map((id) => ({ api: p.api, id, name: orgs.find((o) => o.org_id === id)?.display_name || id })),
  );
  const [openState, setOpen] = useState(false);
  const open = inline || openState;
  const [name, setName] = useState('');
  const [target, setTarget] = useState('user');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fail = (e: unknown) => setErr(e instanceof KinioError ? t(`search.saved_err_${e.code}`, { defaultValue: t('common.op_failed', { code: e.code }) }) : String(e));

  async function save() {
    const org = adminOrgs.find((o) => o.id === target);
    const api = org?.api ?? planes[0]?.api;
    if (!api) return;
    setBusy(true);
    setErr(null);
    try {
      await savedSearchApi.create(api, { scope: org ? 'org' : 'user', org_id: org?.id, name: name.trim(), query, filters });
      setName('');
      await qc.invalidateQueries({ queryKey: ['saved-searches', api] });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove(s: SavedSearch) {
    setBusy(true);
    setErr(null);
    try {
      await savedSearchApi.remove(s.api, s.search_id);
      await qc.invalidateQueries({ queryKey: ['saved-searches', s.api] });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={'saved-search' + (inline ? ' saved-search-inline' : '')}>
      {!inline && (
        <button className="btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {t('search.saved')}
          {all.length > 0 ? `（${all.length}）` : ''}
        </button>
      )}
      {open && (
        <div className={'card' + (inline ? '' : ' saved-search-panel')} role={inline ? 'region' : 'dialog'} aria-label={t('search.saved')}>
          {all.length === 0 ? (
            <p className="muted small">{t('search.saved_none')}</p>
          ) : (
            <ul className="saved-search-list">
              {all.map((s) => (
                <li key={s.api + s.search_id} data-saved={s.search_id}>
                  <button
                    className="link"
                    title={s.query}
                    onClick={() => {
                      onApply(s);
                      setOpen(false);
                    }}
                  >
                    {s.name}
                  </button>
                  {s.scope === 'org' && <span className="badge">{t('search.saved_shared')}</span>}
                  <span className="muted small saved-search-q">{s.query}</span>
                  {s.can_write && (
                    <button
                      className="icon-btn"
                      disabled={busy}
                      aria-label={t('search.saved_delete', { name: s.name })}
                      title={t('search.saved_delete', { name: s.name })}
                      onClick={() => void remove(s)}
                    >
                      ✕
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="toolbar">
            <input
              className="input"
              value={name}
              maxLength={80}
              placeholder={t('search.saved_name')}
              aria-label={t('search.saved_name')}
              onChange={(e) => setName(e.target.value)}
            />
            {adminOrgs.length > 0 && (
              <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} aria-label={t('search.saved_scope')}>
                <option value="user">{t('search.saved_personal')}</option>
                {adminOrgs.map((o) => (
                  <option key={o.api + o.id} value={o.id}>
                    {t('search.saved_org', { name: o.name })}
                  </option>
                ))}
              </select>
            )}
            <button className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void save()}>
              {t('search.saved_save')}
            </button>
          </div>
          <p className="muted small">{t('search.saved_help')}</p>
          {err && <p className="error small">{err}</p>}
        </div>
      )}
    </div>
  );
}
